'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { ChevronRight, Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNotificationStore } from '../../model/notification-store';
import type { NotificationThread } from '../../lib/threading';
import { relativeLabel, untilLabel } from '../../lib/relative-time';
import { hrefFor } from '../../lib/links';

/**
 * Settled things behind a count.
 *
 * Snoozed and finished are both real state and neither belongs in the day: a
 * snoozed item is not on the broker's screen by their own instruction, and a
 * finished one is history. Both stay reachable, both keep their verb, and both
 * cost one line each until opened. On the seeded day that is two snoozed and
 * nine finished, against sixty-eight live.
 *
 * The snooze fold is also where a snooze that keeps coming back becomes
 * visible, which is the plan's answer to the item that hides on a messy desk:
 * it says when each one returns, so a thing that has been pushed three times
 * is three lines the broker can see rather than an absence they cannot.
 */
export function SettledFolds(t0) {
  const $ = _c(7);
  const {
    snoozed,
    done
  } = t0;
  let t1;
  if ($[0] !== snoozed) {
    t1 = <Fold label="Snoozed" blurb="Out of the way by your own instruction, and coming back." threads={snoozed} kind="snoozed" />;
    $[0] = snoozed;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== done) {
    t2 = <Fold label="Finished" blurb="Dealt with. Kept, never deleted." threads={done} kind="done" />;
    $[2] = done;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="space-y-2">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function Fold(t0) {
  const $ = _c(25);
  const {
    label,
    blurb,
    threads,
    kind
  } = t0;
  const [open, setOpen] = useState(false);
  const unsnooze = useNotificationStore(_temp);
  const markUndone = useNotificationStore(_temp2);
  if (threads.length === 0) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(_temp3);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = open && "rotate-90";
  let t3;
  if ($[1] !== t2) {
    t3 = cn("h-3.5 w-3.5 text-[#44546F] dark:text-white/70 transition-transform", t2);
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t3) {
    t4 = <ChevronRight className={t3} />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== label) {
    t5 = <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#091E42] dark:text-white">{label}</span>;
    $[5] = label;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== blurb) {
    t6 = <span className="truncate text-[11px] text-[#44546F] dark:text-white/70">{blurb}</span>;
    $[7] = blurb;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== threads.length) {
    t7 = <span className="ml-auto shrink-0 rounded-full bg-white dark:bg-white/12 px-2 text-[11px] font-medium tabular-nums text-[#091E42] dark:text-white shadow-[0_1px_3px_rgba(15,23,42,0.08)]">{threads.length}</span>;
    $[9] = threads.length;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = <button type="button" onClick={t1} className="flex w-full items-center gap-2 rounded-2xl px-3 py-2 text-left transition-colors hover:bg-white/60 dark:hover:bg-white/6">{t4}{t5}{t6}{t7}</button>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== kind || $[17] !== markUndone || $[18] !== open || $[19] !== threads || $[20] !== unsnooze) {
    t9 = open ? <ul className="space-y-0.5 px-3 pb-2">{threads.map(t => <li key={t.key} className="flex items-baseline gap-2"><a href={hrefFor(t.head)} className="truncate text-[12px] text-[#091E42] dark:text-white hover:underline">{t.head.title}</a><span className="truncate text-[11px] text-[#44546F]/80">{t.head.clientName ?? "The firm"}</span><span className="ml-auto shrink-0 text-[11px] text-[#44546F]/70 dark:text-white/50">{kind === "snoozed" && t.head.snoozedUntil ? untilLabel(t.head.snoozedUntil) : relativeLabel(t.head.doneAt ?? t.head.createdAt)}</span><button type="button" onClick={() => t.items.forEach(n => kind === "snoozed" ? unsnooze(n.id) : markUndone(n.id))} title={kind === "snoozed" ? "Bring it back now" : "Not finished after all"} className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white dark:bg-white/12 text-[#44546F] dark:text-white/70 shadow-[0_1px_3px_rgba(15,23,42,0.08)] transition-shadow hover:text-[#091E42] dark:hover:text-white hover:shadow-[0_2px_8px_rgba(86,81,255,0.15)]"><Undo2 className="h-3 w-3" /></button></li>)}</ul> : null;
    $[16] = kind;
    $[17] = markUndone;
    $[18] = open;
    $[19] = threads;
    $[20] = unsnooze;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = <section className="rounded-2xl bg-white shadow-[0_1px_3px_rgba(15,23,42,0.05)] dark:bg-white/12">{t8}{t9}</section>;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}
function _temp3(v) {
  return !v;
}
function _temp2(s_0) {
  return s_0.markUndone;
}
function _temp(s) {
  return s.unsnooze;
}